/**
 * StreamingSection - Live Streaming & Creator Highlights
 *
 * Displays the hub's streaming team with live streams, theater mode,
 * and a Twitch clips collage when nobody is live.
 *
 * Streaming config is owned by the surface itself (`hub.streamingSettings`).
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import {StreamingApi} from "@devour/client";
import getConfig from "@/utils/getConfig";
import { useQuery } from "@tanstack/react-query";
import { FaVideo, FaTwitch, FaYoutube, FaEye, FaExternalLinkAlt, FaTimes, FaComments, FaVolumeMute, FaVolumeUp, FaPlay } from "react-icons/fa";
import { SiKick } from "react-icons/si";
import Skeleton from "react-loading-skeleton";
import { useStreamViewTracking } from "@/hooks/useStreamViewTracking";
import { StreamWatchTimer } from "@/widgets/StreamWatchTimer";
import { lockScroll, unlockScroll } from "@/utils/scrollLock";
import type { StreamingSettings, FeaturedStreamResponse } from "@/hooks/hub/useGetHub";
import { getChatEmbedUrl, getStreamEmbedUrl, getStreamExternalUrl, sanitize } from "./shared";
import "./StreamingSection.scss";
import {formatCompactNumber} from "@/utils/formatCompactNumber";

interface StreamingSectionProps {
    /** Hub's streaming settings (from hub.streamingSettings) */
    streamingSettings?: StreamingSettings;
    /** Hub name for dynamic title */
    hubName?: string;
    /** Hub type to determine title/description */
    hubType?: "BRAND" | "CREATOR" | "COMMUNITY" | "COMPETITION" | "GENERAL";
    /** Hub ID for stream view tracking */
    surfaceId?: string;
}

interface TwitchClip {
    clipId: string;
    title: string;
    url: string;
    embedUrl: string;
    thumbnailUrl: string;
    viewCount: number;
    creatorName: string;
    createdAt: string;
    duration: number;
    broadcasterName: string;
}

/*
 * A MEMBER MAY HAVE NO PLATFORM.
 *
 * `platform` is a property of a CHANNEL and a roster member is not required to
 * have one — a Finals Qualifier Contestant is on the roster by winning a
 * qualifier, not by streaming. So every read of it here is optional, and the
 * modifier suffix below falls back to a neutral value rather than being
 * interpolated from `undefined`.
 *
 * These sites read `platform` straight off the API response and called
 * `.toLowerCase()` on it unguarded. They compiled only because
 * `hooks/hub/useGetHub.ts` held a hand-written copy of the response type that
 * still declared `platform` required; against the real generated type they are
 * a `TypeError` that takes this whole section down on the first bare member.
 */
const platformClass = (platform?: string): string => platform?.toLowerCase() ?? "unknown";

const getPlatformIcon = (platform?: string) => {
    switch (platform) {
        case "TWITCH":
            return <FaTwitch />;
        case "YOUTUBE":
            return <FaYoutube />;
        case "KICK":
            return <SiKick />;
        default:
            return <FaVideo />;
    }
};

/* `#` keeps this usable as a bare href when a member has no linkable channel. */
const getPlatformUrl = (stream: FeaturedStreamResponse): string =>
    getStreamExternalUrl(stream) ?? "#";

const getEmbedUrl = (stream: FeaturedStreamResponse, muted: boolean = false): string =>
    getStreamEmbedUrl(stream, {muted});

const formatViewerCount = (count: number): string => formatCompactNumber(count);

/** Resolve the platform-specific channel identifier from a FeaturedStream */
function getChannelId(stream: FeaturedStreamResponse): string {
    return stream.twitchChannelId || stream.kickChannelSlug || stream.youtubeChannelId || stream._id;
}

/**
 * Fetch recent Twitch clips for the featured creators
 */
async function fetchTwitchClips(channels: string[]): Promise<TwitchClip[]> {
    if (channels.length === 0) return [];
    try {
        const api = new StreamingApi(getConfig());
        const data = await api.getStreamsClips({channels: channels.join(","), limit: 12});
        return (data.clips || []) as unknown as TwitchClip[];
    } catch (e) {
        console.error("[HubStreaming] Failed to fetch Twitch clips:", e);
    }
    return [];
}

interface YouTubeVideo {
    videoId: string;
    title: string;
    thumbnailUrl: string;
    publishedAt: string;
    viewCount?: number;
    isShort?: boolean;
}

async function fetchYouTubeVideos(channelId: string): Promise<YouTubeVideo[]> {
    try {
        const api = new StreamingApi(getConfig());
        const data = await (api as unknown as { getStreamsYoutubeVideos: (p: { channelId: string; limit: number }) => Promise<{ videos: YouTubeVideo[] }> })
            .getStreamsYoutubeVideos({channelId, limit: 12});
        return data.videos || [];
    } catch (e) {
        console.error("[HubStreaming] Failed to fetch YouTube videos:", e);
    }
    return [];
}

export const StreamingSection = ({ streamingSettings, hubName, hubType, surfaceId }: StreamingSectionProps) => {
    const [activeStream, setActiveStream] = useState<FeaturedStreamResponse | null>(null);
    const [showChat, setShowChat] = useState(true);
    /*
     * Starts MUTED, because the Creator-hub inline player below auto-mounts
     * when the creator is live with no user click, and browsers refuse unmuted
     * autoplay without a prior interaction signal — an unmuted auto-mount
     * renders a paused player and nothing moves. Same reasoning, and the same
     * fix, as `stream-panel/buildInlineSource.ts`.
     *
     * The click paths unmute it: `handleStreamClick` and `handleClipClick` open
     * the theater from a real interaction, which is not subject to autoplay
     * policy, so those keep their sound. Both surfaces' mute toggles drive this
     * same value, so the viewer can still change it either way.
     */
    const [isMuted, setIsMuted] = useState(true);
    const [isTheaterOpen, setIsTheaterOpen] = useState(false);
    const [activeClip, setActiveClip] = useState<TwitchClip | null>(null);
    const { onTheaterOpen, onTheaterClose, elapsedSeconds } = useStreamViewTracking();

    const handleCloseTheater = useCallback(() => {
        setIsTheaterOpen(false);
        setActiveStream(null);
        setActiveClip(null);
        onTheaterClose();
    }, [onTheaterClose]);

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.key === "Escape" && (isTheaterOpen || activeClip)) {
            handleCloseTheater();
        }
    }, [isTheaterOpen, activeClip, handleCloseTheater]);

    useEffect(() => {
        if (isTheaterOpen || activeClip) {
            document.addEventListener("keydown", handleKeyDown);
            lockScroll();
        }
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            unlockScroll();
        };
    }, [isTheaterOpen, activeClip, handleKeyDown]);

    // Featured streams from hub's streaming settings
    const featuredStreams = useMemo(
        () => (streamingSettings?.featuredStreams || []).sort((a, b) => a.priority - b.priority),
        [streamingSettings?.featuredStreams],
    );
    const showViewerCounts = streamingSettings?.showViewerCounts ?? true;

    // Separate live from offline
    const liveStreams = useMemo(() => featuredStreams.filter(s => s.isLive), [featuredStreams]);
    const offlineStreams = useMemo(() => featuredStreams.filter(s => !s.isLive), [featuredStreams]);

    // Extract Twitch channel IDs for clips fetch
    const twitchChannels = useMemo(
        () => featuredStreams
            .filter(s => s.platform === "TWITCH" && s.twitchChannelId)
            .map(s => s.twitchChannelId!),
        [featuredStreams],
    );

    // Extract YouTube channel ID for offline content
    const youtubeChannelId = useMemo(
        () => featuredStreams.find(s => s.platform === "YOUTUBE" && s.youtubeChannelId)?.youtubeChannelId,
        [featuredStreams],
    );

    // Fetch Twitch clips when nobody is live
    const { data: twitchClips } = useQuery({
        queryKey: ["twitch-clips", twitchChannels.join(",")],
        queryFn: () => fetchTwitchClips(twitchChannels),
        enabled: liveStreams.length === 0 && twitchChannels.length > 0,
        staleTime: 5 * 60 * 1000,
    });

    // Fetch YouTube videos when nobody is live — keeps viewers on hub instead of sending to YouTube
    const { data: youtubeVideos } = useQuery({
        queryKey: ["youtube-videos", youtubeChannelId],
        queryFn: () => fetchYouTubeVideos(youtubeChannelId!),
        enabled: liveStreams.length === 0 && !!youtubeChannelId,
        staleTime: 10 * 60 * 1000,
    });

    const [activeYouTubeVideo, setActiveYouTubeVideo] = useState<YouTubeVideo | null>(null);

    // Dynamic section title based on hub type
    const sectionContent = useMemo(() => {
        switch (hubType) {
            case "CREATOR":
                return {
                    title: hubName ? `Watch ${hubName}` : "Watch Live",
                    description: hubName ? `Catch ${hubName}'s latest streams` : "Watch live streams",
                };
            case "BRAND":
                return {
                    title: "Featured Creators",
                    description: "Watch featured streams and community content",
                };
            case "COMMUNITY":
                return {
                    title: "Community Streams",
                    description: "Watch streams from community members",
                };
            case "COMPETITION":
                return {
                    title: "Live Streams",
                    description: "Watch tournament and competition streams",
                };
            default:
                return {
                    title: "Live Streams",
                    description: "Watch live streams",
                };
        }
    }, [hubType, hubName]);

    const handleStreamClick = (stream: FeaturedStreamResponse) => {
        setActiveStream(stream);
        setActiveClip(null);
        setIsTheaterOpen(true);
        /* Opened by a real click, so autoplay policy doesn't apply — give it sound. */
        setIsMuted(false);
        onTheaterOpen({
            platform: stream.platform,
            channelId: getChannelId(stream),
            surfaceId,
        });
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            handleCloseTheater();
        }
    };

    const handleSwitchStream = (stream: FeaturedStreamResponse) => {
        setActiveStream(stream);
        setActiveClip(null);
        onTheaterOpen({
            platform: stream.platform,
            channelId: getChannelId(stream),
            surfaceId,
        });
    };

    const handleClipClick = (clip: TwitchClip) => {
        setActiveClip(clip);
        setActiveStream(null);
        setIsTheaterOpen(true);
        setIsMuted(false);
    };

    // For Creator hubs: Show inline player when the creator is live
    const isCreatorHub = hubType === "CREATOR";
    const creatorLiveStream = isCreatorHub && liveStreams.length > 0 ? liveStreams[0] : null;
    const creatorChatUrl = creatorLiveStream ? getChatEmbedUrl(creatorLiveStream) : null;

    /*
     * Track watch time for Creator hub inline player. The inline player auto-renders
     * when the creator is live (no user click needed), so we start tracking on mount
     * and stop when the stream goes offline or the component unmounts.
     */
    useEffect(() => {
        if (!creatorLiveStream) return;

        onTheaterOpen({
            platform: creatorLiveStream.platform,
            channelId: getChannelId(creatorLiveStream),
            surfaceId,
        });

        return () => {
            onTheaterClose();
        };
    // Track based on the creator's stream identity, not the object reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [creatorLiveStream?._id]);

    // Don't render if streaming is explicitly disabled or no settings exist
    if (!streamingSettings?.enabled && featuredStreams.length === 0) {
        return null;
    }

    const chatUrl = activeStream ? getChatEmbedUrl(activeStream) : null;

    // Creator Hub LIVE - Render inline theater mode (no overlay needed)
    if (isCreatorHub && creatorLiveStream) {
        return (
            <section className="section streaming-section streaming-section--creator-live">
                <div className="section__header">
                    <h2>
                        <FaVideo />
                        {sectionContent.title}
                    </h2>
                    <p>{sectionContent.description}</p>
                </div>

                <div className="streaming-section__content">
                    <div className="streaming-section__inline-theater">
                        <div className="streaming-section__inline-header">
                            <div className="streaming-section__inline-info">
                                <span className="streaming-section__live-indicator">
                                    <span className="streaming-section__live-dot" />
                                    LIVE
                                </span>
                                <div className={`streaming-section__inline-platform streaming-section__inline-platform--${platformClass(creatorLiveStream.platform)}`}>
                                    {getPlatformIcon(creatorLiveStream.platform)}
                                </div>
                                {showViewerCounts && creatorLiveStream.viewerCount > 0 &&
                                    <span className="streaming-section__inline-viewers">
                                        <FaEye /> {formatViewerCount(creatorLiveStream.viewerCount)} watching
                                    </span>
                                }
                                <StreamWatchTimer elapsedSeconds={elapsedSeconds} />
                            </div>
                            <div className="streaming-section__inline-controls">
                                <button
                                    className={`streaming-section__inline-btn ${isMuted ? "streaming-section__inline-btn--active" : ""}`}
                                    onClick={() => setIsMuted(!isMuted)}
                                    title={isMuted ? "Unmute" : "Mute"}
                                >
                                    {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
                                </button>
                                {creatorChatUrl &&
                                    <button
                                        className={`streaming-section__inline-btn ${showChat ? "streaming-section__inline-btn--active" : ""}`}
                                        onClick={() => setShowChat(!showChat)}
                                        title={showChat ? "Hide Chat" : "Show Chat"}
                                    >
                                        <FaComments />
                                    </button>
                                }
                                <a
                                    href={getPlatformUrl(creatorLiveStream)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="streaming-section__inline-btn"
                                    title={creatorLiveStream.platform ? `Open on ${creatorLiveStream.platform}` : "Open externally"}
                                >
                                    <FaExternalLinkAlt />
                                </a>
                            </div>
                        </div>

                        <div className={`streaming-section__inline-main ${showChat && creatorChatUrl ? "" : "streaming-section__inline-main--no-chat"}`}>
                            <div className="streaming-section__inline-player">
                                <iframe
                                    src={getEmbedUrl(creatorLiveStream, isMuted)}
                                    frameBorder="0"
                                    allowFullScreen
                                    allow="autoplay; fullscreen"
                                    title={`${creatorLiveStream.label} stream`}
                                />
                            </div>
                            {showChat && creatorChatUrl &&
                                <div className="streaming-section__inline-chat">
                                    <iframe
                                        src={creatorChatUrl}
                                        frameBorder="0"
                                        title={`${creatorLiveStream.label} chat`}
                                    />
                                </div>
                            }
                        </div>
                    </div>

                    {offlineStreams.length > 0 &&
                        <div className="streaming-section__other-channels">
                            <h3 className="streaming-section__section-title">
                                Other Channels
                            </h3>
                            <div className="streaming-section__channels-grid">
                                {offlineStreams.map((stream) =>
                                    <a
                                        key={stream._id}
                                        href={getPlatformUrl(stream)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="streaming-section__channel"
                                    >
                                        <div className={`streaming-section__channel-icon streaming-section__channel-icon--${platformClass(stream.platform)}`}>
                                            {getPlatformIcon(stream.platform)}
                                        </div>
                                        <span className="streaming-section__channel-name">
                                            {stream.label}
                                        </span>
                                    </a>)}
                            </div>
                        </div>
                    }
                </div>
            </section>
        );
    }

    return (
        <React.Fragment>
            {/* Theater Mode Overlay — streams or clips */}
            {isTheaterOpen && (activeStream || activeClip) &&
                <div
                    className="streaming-section__theater"
                    onClick={handleOverlayClick}
                >
                    <div className="streaming-section__theater-content">
                        {/* Header */}
                        <div className="streaming-section__theater-header">
                            <div className="streaming-section__theater-info">
                                {activeStream &&
                                    <>
                                        <div className={`streaming-section__theater-platform streaming-section__theater-platform--${platformClass(activeStream.platform)}`}>
                                            {getPlatformIcon(activeStream.platform)}
                                        </div>
                                        <span className="streaming-section__theater-name">{activeStream.label}</span>
                                        {showViewerCounts && activeStream.viewerCount > 0 &&
                                            <span className="streaming-section__theater-viewers">
                                                <FaEye /> {formatViewerCount(activeStream.viewerCount)} watching
                                            </span>
                                        }
                                        <StreamWatchTimer elapsedSeconds={elapsedSeconds} />
                                    </>
                                }
                                {activeClip &&
                                    <>
                                        <div className="streaming-section__theater-platform streaming-section__theater-platform--twitch">
                                            <FaTwitch />
                                        </div>
                                        <span className="streaming-section__theater-name">{activeClip.broadcasterName}</span>
                                        <span className="streaming-section__theater-clip-title">{activeClip.title}</span>
                                    </>
                                }
                            </div>
                            <div className="streaming-section__theater-controls">
                                {activeStream &&
                                    <>
                                        <button
                                            className={`streaming-section__theater-btn ${isMuted ? "streaming-section__theater-btn--active" : ""}`}
                                            onClick={() => setIsMuted(!isMuted)}
                                            title={isMuted ? "Unmute" : "Mute"}
                                        >
                                            {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
                                        </button>
                                        {chatUrl &&
                                            <button
                                                className={`streaming-section__theater-btn ${showChat ? "streaming-section__theater-btn--active" : ""}`}
                                                onClick={() => setShowChat(!showChat)}
                                                title={showChat ? "Hide Chat" : "Show Chat"}
                                            >
                                                <FaComments />
                                            </button>
                                        }
                                        <a
                                            href={getPlatformUrl(activeStream)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="streaming-section__theater-btn"
                                            title={activeStream.platform ? `Open on ${activeStream.platform}` : "Open externally"}
                                        >
                                            <FaExternalLinkAlt />
                                        </a>
                                    </>
                                }
                                {activeClip &&
                                    <a
                                        href={activeClip.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="streaming-section__theater-btn"
                                        title="Open on Twitch"
                                    >
                                        <FaExternalLinkAlt />
                                    </a>
                                }
                                <button
                                    className="streaming-section__theater-btn streaming-section__theater-btn--close"
                                    onClick={handleCloseTheater}
                                    title="Close (ESC)"
                                >
                                    <FaTimes />
                                </button>
                            </div>
                        </div>

                        {/* Main Content - Player + Chat */}
                        <div className={`streaming-section__theater-main ${activeStream && showChat && chatUrl ? "" : "streaming-section__theater-main--no-chat"}`}>
                            <div className="streaming-section__theater-player">
                                {activeStream &&
                                    <iframe
                                        src={getEmbedUrl(activeStream, isMuted)}
                                        frameBorder="0"
                                        allowFullScreen
                                        allow="autoplay; fullscreen"
                                        title={`${activeStream.label} stream`}
                                    />
                                }
                                {activeClip && activeClip.clipId &&
                                    <iframe
                                        /* muted: false — the viewer clicked this clip tile, so it keeps its sound. */
                                        src={getStreamEmbedUrl({platform: "TWITCH", twitchClipSlug: activeClip.clipId}, {muted: false})}
                                        frameBorder="0"
                                        allowFullScreen
                                        allow="autoplay; fullscreen"
                                        title={`${activeClip.title} clip`}
                                    />
                                }
                            </div>
                            {activeStream && showChat && chatUrl &&
                                <div className="streaming-section__theater-chat">
                                    <iframe
                                        src={chatUrl}
                                        frameBorder="0"
                                        title={`${activeStream.label} chat`}
                                    />
                                </div>
                            }
                        </div>

                        {/* Stream Selector Bar */}
                        {liveStreams.length > 1 &&
                            <div className="streaming-section__theater-selector">
                                <span className="streaming-section__theater-selector-label">
                                    Switch Stream:
                                </span>
                                <div className="streaming-section__theater-selector-streams">
                                    {liveStreams.map((stream) =>
                                        <button
                                            key={stream._id}
                                            onClick={() => handleSwitchStream(stream)}
                                            className={`streaming-section__theater-selector-item ${activeStream?._id === stream._id ? "streaming-section__theater-selector-item--active" : ""}`}
                                        >
                                            <div className={`streaming-section__theater-selector-icon streaming-section__theater-selector-icon--${platformClass(stream.platform)}`}>
                                                {getPlatformIcon(stream.platform)}
                                            </div>
                                            <span>{stream.label}</span>
                                            {showViewerCounts && stream.viewerCount > 0 &&
                                                <span className="streaming-section__theater-selector-viewers">
                                                    {formatViewerCount(stream.viewerCount)}
                                                </span>
                                            }
                                        </button>)}
                                </div>
                            </div>
                        }
                    </div>
                </div>
            }

            <section className="section streaming-section">
                <div className="section__header">
                    <h2>
                        <FaVideo />
                        {sectionContent.title}
                    </h2>
                    <p>{sectionContent.description}</p>
                </div>

                <div className="streaming-section__content">

                    {/* Live Now Section */}
                    {liveStreams.length > 0 &&
                        <div className="streaming-section__live-section">
                            <h3 className="streaming-section__section-title">
                                <span className="streaming-section__live-dot" />
                                Live Now
                            </h3>
                            <div className="streaming-section__grid">
                                {liveStreams.map((stream) =>
                                    <button
                                        key={stream._id}
                                        type="button"
                                        onClick={() => handleStreamClick(stream)}
                                        className={`streaming-section__stream streaming-section__stream--live ${activeStream?._id === stream._id ? "streaming-section__stream--active" : ""}`}
                                    >
                                        <div className="streaming-section__stream-preview">
                                            {stream.thumbnailUrl
                                                ? <img src={stream.thumbnailUrl} alt={stream.label} />
                                                : <div className="streaming-section__stream-placeholder">
                                                    {getPlatformIcon(stream.platform)}
                                                </div>
                                            }
                                            <div className="streaming-section__stream-live-badge">
                                                <span>LIVE</span>
                                            </div>
                                            {showViewerCounts && stream.viewerCount > 0 &&
                                                <div className="streaming-section__stream-viewers-badge">
                                                    <FaEye /> {formatViewerCount(stream.viewerCount)}
                                                </div>
                                            }
                                        </div>
                                        <div className="streaming-section__stream-info">
                                            <div className={`streaming-section__stream-platform streaming-section__stream-platform--${platformClass(stream.platform)}`}>
                                                {getPlatformIcon(stream.platform)}
                                            </div>
                                            <div className="streaming-section__stream-meta">
                                                <span className="streaming-section__stream-name">
                                                    {stream.label}
                                                </span>
                                            </div>
                                        </div>
                                    </button>)}
                            </div>
                        </div>
                    }

                    {/* YouTube videos — inline playback keeps viewers on hub when creator is offline */}
                    {liveStreams.length === 0 && youtubeVideos && youtubeVideos.length > 0 &&
                        <div className="streaming-section__clips-section">
                            <h3 className="streaming-section__section-title">
                                <FaYoutube /> Latest Videos
                            </h3>
                            {activeYouTubeVideo &&
                                <div className="streaming-section__inline-player streaming-section__inline-player--youtube">
                                    <div className="streaming-section__inline-header">
                                        <span className="streaming-section__inline-info">{activeYouTubeVideo.title}</span>
                                        <button
                                            className="streaming-section__inline-btn streaming-section__inline-btn--close"
                                            onClick={() => setActiveYouTubeVideo(null)}
                                        ><FaTimes /></button>
                                    </div>
                                    <iframe
                                        src={`https://www.youtube.com/embed/${sanitize(activeYouTubeVideo.videoId)}?autoplay=1&rel=0`}
                                        frameBorder="0"
                                        allowFullScreen
                                        allow="autoplay; fullscreen"
                                        title={activeYouTubeVideo.title}
                                    />
                                </div>
                            }
                            <div className="streaming-section__clips-grid">
                                {youtubeVideos.slice(0, 6).map((video) =>
                                    <button
                                        key={video.videoId}
                                        type="button"
                                        onClick={() => setActiveYouTubeVideo(video)}
                                        className="streaming-section__clip"
                                    >
                                        <div className="streaming-section__clip-preview">
                                            <img src={video.thumbnailUrl} alt={video.title} />
                                            <div className="streaming-section__clip-play">
                                                <FaPlay />
                                            </div>
                                            {video.isShort &&
                                                <div className="streaming-section__clip-duration">Short</div>
                                            }
                                            {video.viewCount &&
                                                <div className="streaming-section__clip-views">
                                                    <FaEye /> {formatViewerCount(video.viewCount)}
                                                </div>
                                            }
                                        </div>
                                        <div className="streaming-section__clip-info">
                                            <span className="streaming-section__clip-title">{video.title}</span>
                                        </div>
                                    </button>)}
                            </div>
                        </div>
                    }

                    {/* Creator Highlights — Twitch clips collage when nobody is live */}
                    {liveStreams.length === 0 && twitchClips && twitchClips.length > 0 &&
                        <div className="streaming-section__clips-section">
                            <h3 className="streaming-section__section-title">
                                <FaTwitch /> Creator Highlights
                            </h3>
                            <div className="streaming-section__clips-grid">
                                {twitchClips.slice(0, 6).map((clip) =>
                                    <button
                                        key={clip.clipId}
                                        type="button"
                                        onClick={() => handleClipClick(clip)}
                                        className="streaming-section__clip"
                                    >
                                        <div className="streaming-section__clip-preview">
                                            <img src={clip.thumbnailUrl} alt={clip.title} />
                                            <div className="streaming-section__clip-play">
                                                <FaPlay />
                                            </div>
                                            <div className="streaming-section__clip-duration">
                                                {Math.floor(clip.duration)}s
                                            </div>
                                            <div className="streaming-section__clip-views">
                                                <FaEye /> {formatViewerCount(clip.viewCount)}
                                            </div>
                                        </div>
                                        <div className="streaming-section__clip-info">
                                            <span className="streaming-section__clip-title">{clip.title}</span>
                                            <span className="streaming-section__clip-creator">{clip.broadcasterName}</span>
                                        </div>
                                    </button>)}
                            </div>
                        </div>
                    }

                    {/* Featured Channels — always visible */}
                    {featuredStreams.length > 0 &&
                        <div className="streaming-section__featured-section">
                            <h3 className="streaming-section__section-title">
                                Featured Channels
                            </h3>
                            <div className="streaming-section__channels-grid">
                                {featuredStreams.map((stream) =>
                                    <a
                                        key={stream._id}
                                        href={getPlatformUrl(stream)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`streaming-section__channel ${stream.isLive ? "streaming-section__channel--live" : ""}`}
                                    >
                                        <div className={`streaming-section__channel-icon streaming-section__channel-icon--${platformClass(stream.platform)}`}>
                                            {getPlatformIcon(stream.platform)}
                                        </div>
                                        <span className="streaming-section__channel-name">
                                            {stream.label}
                                        </span>
                                        <span className={`streaming-section__channel-status ${stream.isLive ? "streaming-section__channel-status--live" : ""}`}>
                                            {stream.isLive ? "LIVE" : "Offline"}
                                        </span>
                                    </a>)}
                            </div>
                        </div>
                    }

                    {/* No streams configured at all */}
                    {featuredStreams.length === 0 &&
                        <div className="streaming-section__no-streams">
                            <FaVideo size={32} />
                            <p>No featured streams configured</p>
                            <span>Streams will appear here when added by the admin</span>
                        </div>
                    }
                </div>
            </section>
        </React.Fragment>
    );
};
